
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action } from "vuex-class"

import { CaseColumn } from "@/types/caseColumns"

// @ts-ignore
import draggable from "vuedraggable"
import vSelect from "vue-select"
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import ColumnFormatting from "./ColumnFormatting.vue"
import { DataType, FormattingRule } from "@/types/insights"

@Component({
	components: {
		draggable,
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"column-formatting": ColumnFormatting
	}
})
export default class ColumnSelect extends Vue {
	@Prop({ default: () => [] })
	columns!: CaseColumn[]

	@Prop({ default: () => [] })
	value!: string[]

	@Prop({ default: () => { return {} } })
	formattingRules!: { [key: string]: FormattingRule[] }

	currentValue: string[] = []
	currentFormattingRules: { [key: string]: FormattingRule[] } | null = null

	addColumn: CaseColumn | null = null
	editFormattingColumn: CaseColumn | null = null

	get visible (): boolean {
		return !!this.editFormattingColumn
	}
	set visible (value: boolean) {
		if (!value) {
			this.editFormattingColumn = null
			this.$emit("hide")
		}
	}

	mounted () {
		this.currentValue = this.value.filter(c => this.columns.some(column => column.key === c))
		if (this.currentValue.length < this.value.length) {
			this.$emit("input", this.currentValue)
		}

		this.currentFormattingRules = this.formattingRules ? this.formattingRules : {}
	}

	@Watch("value")
	onValueChanged (newValue: string[]) {
		this.currentValue = newValue
	}

	@Watch("formattingRules")
	onFormattingRulesChanged (formattingRules: { [key: string]: FormattingRule[] } | null) {
		this.currentFormattingRules = formattingRules || {}
	}

	get dragOptions () {
		return {
			animation: 200,
			disabled: false,
			ghostClass: "ghost"
		}
	}

	get columnsByKey (): { [key: string]: CaseColumn } {
		return this.columns.toDictionary("key")
	}

	get unaddedColumns (): CaseColumn[] {
		return this.columns.filter(c => !this.currentValue.includes(c.key))
	}

	get columnValues () {
		const columnsByKey = this.columnsByKey
		return this.currentValue.map(col => {
			if (columnsByKey[col]) {
				return columnsByKey[col]
			} else {
				return null
			}
		}).filter(col => col !== null)
	}

	get editFormattingRules (): FormattingRule[] {
		return this.editFormattingColumn
			? this.currentFormattingRules![this.editFormattingColumn!.key]
			: []
	}

	dragEnd () {
		this.$emit("input", this.currentValue)
	}

	addNewColumn (newColumn: CaseColumn | null): void {
		if (newColumn) {
			this.currentValue.push(newColumn.key)
			this.$emit("input", this.currentValue)
		}
		this.addColumn = null
	}

	removeColumn (column: CaseColumn): void {
		this.currentValue.removeWhere(key => key === column.key)
		this.$emit("input", this.currentValue)

		if (this.currentFormattingRules && this.currentFormattingRules[column.key]) {
			delete this.currentFormattingRules[column.key]
			this.$emit("inputFormatting", this.currentFormattingRules)
		}
	}

	setFormatting (column: CaseColumn) {
		if (!this.currentFormattingRules![column.key]) {
			Vue.set(this.currentFormattingRules!, column.key, [])
		}
		this.editFormattingColumn = column
	}

	updateFormattingRules (rules: FormattingRule[]) {
		if (this.editFormattingColumn) {
			Vue.set(this.currentFormattingRules!, this.editFormattingColumn.key, rules)
		}
		this.$emit("inputFormatting", this.currentFormattingRules)
	}

	supportsFormatting (fieldDataType: DataType) {
		return fieldDataType !== DataType.arrayData && fieldDataType !== DataType.none && fieldDataType !== DataType.mapPoint
	}
}

